import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#rememberSizeGuideTrigger, true);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#rememberSizeGuideTrigger, true);
    this.#removeSizeGuideFocusListeners();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #sizeGuideTrigger = null;
  #sizeGuideClosing = false;

  get #isSizeGuide() {
    return this.matches('.popup-link') && !!this.refs.dialog.querySelector('[data-qomoor-size-guide-popup]');
  }

  // Capture the real inline click before its handler forwards a synthetic click.
  #rememberSizeGuideTrigger = (event) => {
    if (!this.#isSizeGuide || !event.isTrusted || this.refs.dialog.open) return;
    const trigger = event.target.closest?.('[data-qomoor-size-guide-trigger], .popup-link__button');
    if (trigger && (trigger.matches('[data-qomoor-size-guide-trigger]') || this.contains(trigger))) {
      this.#sizeGuideTrigger = trigger;
    }
  };

  #sizeGuideFocusable() {
    return [...this.refs.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')].filter(
      (element) => !element.disabled && element.tabIndex >= 0 &&
        !element.closest('[hidden], [inert]') && element.getClientRects().length > 0 &&
        getComputedStyle(element).visibility !== 'hidden'
    );
  }

  #focusSizeGuideStart = () => {
    const { dialog } = this.refs;
    const controls = this.#sizeGuideFocusable();
    const close = controls.find((element) => element.matches('.popup-link__close'));
    const target = close || controls[0] || dialog;
    if (target === dialog && !dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  #handleSizeGuideKeyDown = (event) => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDialog();
    } else if (event.key === 'Tab') {
      const controls = this.#sizeGuideFocusable();
      const first = controls[0];
      const last = controls.at(-1);
      const active = document.activeElement;
      if (!controls.length || !dialog.contains(active) || active === dialog ||
          (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus({ preventScroll: true });
        if (!controls.length) this.#focusSizeGuideStart();
      }
    }
  };

  #handleSizeGuideFocus = (event) => {
    if (this.refs.dialog.open && !this.refs.dialog.contains(event.target)) this.#focusSizeGuideStart();
  };

  #handleSizeGuideCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #removeSizeGuideFocusListeners() {
    document.removeEventListener('keydown', this.#handleSizeGuideKeyDown, true);
    document.removeEventListener('focusin', this.#handleSizeGuideFocus, true);
    this.refs.dialog.removeEventListener('cancel', this.#handleSizeGuideCancel);
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (this.#isSizeGuide && !this.#sizeGuideTrigger?.isConnected) {
      const active = document.activeElement;
      this.#sizeGuideTrigger = active?.matches('[data-qomoor-size-guide-trigger], .popup-link__button')
        ? active : this.querySelector('.popup-link__button');
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      if (this.#isSizeGuide) {
        document.addEventListener('keydown', this.#handleSizeGuideKeyDown, true);
        document.addEventListener('focusin', this.#handleSizeGuideFocus, true);
        dialog.addEventListener('cancel', this.#handleSizeGuideCancel);
        this.#focusSizeGuideStart();
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    const sizeGuide = this.#isSizeGuide;
    if (sizeGuide && this.#sizeGuideClosing) return;
    if (sizeGuide) this.#sizeGuideClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    if (sizeGuide) {
      this.#removeSizeGuideFocusListeners();
      this.#sizeGuideClosing = false;
      const trigger = this.#sizeGuideTrigger;
      this.#sizeGuideTrigger = null;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

